import React from 'react'
import {
  Br,
  Cut,
  Line,
  Printer,
  render,
  Row,
  Text,
} from 'react-thermal-printer'
import { formatPrice } from '@/lib/utils'

interface OrderSummary {
  cart_items: any
  cart: any
}

interface PrinterEndpoint {
  direction: string
  endpointNumber: number
}

interface USBPrinter extends USBDevice {
  configuration: USBConfiguration & {
    interfaces: {
      alternate: {
        endpoints: PrinterEndpoint[]
      }
    }[]
  }
}

export const useKotPrinter = () => {
  const [printer, setPrinter] = React.useState<USBPrinter | null>(null)

  const requestPrinter = async (): Promise<USBPrinter | null> => {
    try {
      const device = (await navigator.usb.requestDevice({
        filters: [
          {
            vendorId: 0x0456, // Vendor ID for the printer
          },
        ],
      })) as USBPrinter
      setPrinter(device)
      return device
    } catch (e) {
      console.error('Error connecting to device:', e)
      return null
    }
  }

  const getPrinter = async (): Promise<USBPrinter | null> => {
    try {
      const devices = await navigator.usb.getDevices()
      if (devices.length > 0) {
        const device = devices[0] as USBPrinter
        setPrinter(device)
        return device
      }
      return null
    } catch (e) {
      console.error('Error getting devices:', e)
      return null
    }
  }

  const template = ({ cart_items, cart }: OrderSummary) => {
    // Early return if required data is missing
    if (!cart_items) return null

    return (
      <Printer type="star" width={48}>
        <Text align="center" size={{ width: 1, height: 2 }} bold={true}>
          KOT Order #{cart?.id}
        </Text>

        <Line />

        <Row left={<Text>Time</Text>} right={<Text>{cart?.created_at}</Text>} />

        <Line />

        {Array.isArray(cart_items) &&
          cart_items.map((item, index) => (
            <React.Fragment key={`item-${index}`}>
              <Row
                left={
                  <Text>
                    {item.product_name} / {item.sku}
                  </Text>
                }
                right={<Text bold={true}>{item?.quantity}</Text>}
                gap={5}
              />
              {item.addon_options?.map((addon: any, addonIndex: number) => (
                <Row
                  key={`addon-${index}-${addonIndex}`}
                  left={
                    <Text size={{ width: 1, height: 1 }}>+ {addon.name}</Text>
                  }
                  right=""
                  gap={5}
                />
              ))}
              <Line />
            </React.Fragment>
          ))}

        <Row
          left={<Text>Table</Text>}
          right={<Text bold={true}>{cart?.restaurant_table}</Text>}
          gap={5}
        />
        <Row
          left={<Text>Covers</Text>}
          right={<Text bold={true}>{cart?.covers}</Text>}
          gap={5}
        />
        <Row
          left={<Text>Type</Text>}
          right={<Text bold={true}>{cart?.type}</Text>}
          gap={5}
        />

        <Line />
        <Text align="center">System by et.lk</Text>
        <Cut />
      </Printer>
    )
  }

  const printReceipt = async (order: OrderSummary): Promise<void> => {
    try {
      let currentPrinter = await getPrinter()

      if (!currentPrinter) {
        currentPrinter = await requestPrinter()
        if (!currentPrinter) {
          throw new Error('No printer available')
        }
      }

      // Render template in a separate operation
      const printData: Uint8Array = await new Promise((resolve, reject) => {
        setTimeout(async () => {
          try {
            const templateResult = template(order)
            if (!templateResult) {
              reject(new Error('Failed to generate template'))
              return
            }
            const rendered = await render(templateResult)
            resolve(rendered)
          } catch (error) {
            reject(error)
          }
        }, 0)
      })

      await currentPrinter.open()
      await currentPrinter.selectConfiguration(1)
      await currentPrinter.claimInterface(0)

      const endpoint =
        currentPrinter.configuration.interfaces[0].alternate.endpoints.find(
          (ep: PrinterEndpoint) => ep.direction === 'out',
        )

      if (!endpoint) {
        throw new Error('No valid endpoint found')
      }

      await currentPrinter.transferOut(endpoint.endpointNumber, printData)
      await currentPrinter.close()
    } catch (e) {
      console.error('Error printing receipt:', e)
      throw e
    }
  }

  return {
    printer,
    requestPrinter,
    getPrinter,
    template,
    printReceipt,
  }
}
